import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Tabs, Form, Input, DatePicker, Button, Space, Typography, Alert } from "antd";
import dayjs from "dayjs";
import axiosInstance from "../../api/axiosInstance";
import { useAuth } from "../../context/AuthContext";
import LegalRichEditor from "../../components/LegalRichEditor";
import notify from "../../utils/notify";

const { Title } = Typography;

const TYPE_TERMS = 1;   // LegalDocumentType.TermsOfUse
const TYPE_PRIVACY = 2; // LegalDocumentType.PrivacyPolicy

// Helper: validate Quill HTML has meaningful text
const hasMeaningfulText = (html) => {
  const plain = String(html || "")
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/<[^>]+>/g, "")
    .replace(/\u00A0/g, " ")
    .trim();
  return plain.length > 0;
};

export default function AdminLegalDocsPage() {
  const { authData } = useAuth();
  const token = authData?.token;

  const authHeader = useMemo(() => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const [form] = Form.useForm();
  const [docs, setDocs] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setBusy(true);
      const res = await axiosInstance.get("/legal/current"); // public endpoint
      setDocs(res.data);

      form.setFieldsValue({
        termsTitle: res.data?.terms?.title || "Terms of Use",
        termsEffective: res.data?.terms?.effectiveFromUtc ? dayjs(res.data.terms.effectiveFromUtc) : dayjs(),
        termsHtml: res.data?.terms?.contentHtml || "",

        privacyTitle: res.data?.privacy?.title || "Privacy Policy",
        privacyEffective: res.data?.privacy?.effectiveFromUtc ? dayjs(res.data.privacy.effectiveFromUtc) : dayjs(),
        privacyHtml: res.data?.privacy?.contentHtml || "",
      });
    } catch (e) {
      notify.error(e?.response?.data?.message || "Failed to load legal documents.");
    } finally {
      setBusy(false);
    }
  }, [form]);

  useEffect(() => {
    load();
  }, [load]);

  const upsert = useCallback(
    async (type, publishNewVersion) => {
      if (!token) {
        notify.error("You are not authenticated.");
        return;
      }

      const v = await form.validateFields();

      const isTerms = type === TYPE_TERMS;
      const title = isTerms ? v.termsTitle : v.privacyTitle;
      const html = isTerms ? v.termsHtml : v.privacyHtml;
      const effective = (isTerms ? v.termsEffective : v.privacyEffective)?.toISOString?.();

      const payload = {
        type,
        title,
        contentHtml: html,
        effectiveFromUtc: effective,
        publishNewVersion,
      };

      try {
        setBusy(true);
        await axiosInstance.post("/admin/legal/upsert", payload, { headers: authHeader });
        notify.success(publishNewVersion ? "Published new version." : "Saved.");
        await load();
      } catch (e) {
        notify.error(e?.response?.data?.message || "Failed to save.");
      } finally {
        setBusy(false);
      }
    },
    [authHeader, form, load, token]
  );

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 16px" }}>
      <Card bordered>
        <Title level={3} style={{ marginTop: 0 }}>
          Legal Documents Admin
        </Title>

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 14 }}
          message="How publishing works"
          description="Save updates the current version. Publish creates a NEW version and will force users to accept again."
        />

        <Form layout="vertical" form={form}>
          <Tabs
            items={[
              {
                key: "terms",
                label: `Terms of Use${docs?.terms?.version ? ` (v${docs.terms.version})` : ""}`,
                children: (
                  <>
                    <Form.Item
                      name="termsTitle"
                      label="Title"
                      rules={[{ required: true, message: "Title is required." }]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      name="termsEffective"
                      label="Effective date (UTC)"
                      rules={[{ required: true, message: "Effective date is required." }]}
                    >
                      <DatePicker showTime style={{ width: "100%" }} />
                    </Form.Item>

                    <Form.Item
                      name="termsHtml"
                      label="Content"
                      rules={[
                        {
                          validator: (_, html) =>
                            hasMeaningfulText(html)
                              ? Promise.resolve()
                              : Promise.reject(new Error("Terms content is required.")),
                        },
                      ]}
                      valuePropName="value"
                      trigger="onChange"
                      getValueFromEvent={(html) => html} // ReactQuill: first arg is html string
                    >
                      <LegalRichEditor minHeight={320} />
                    </Form.Item>

                    <Space>
                      <Button onClick={() => upsert(TYPE_TERMS, false)} loading={busy}>
                        Save (no version bump)
                      </Button>
                      <Button type="primary" onClick={() => upsert(TYPE_TERMS, true)} loading={busy}>
                        Publish new version
                      </Button>
                      <Button onClick={load} loading={busy}>
                        Reload
                      </Button>
                    </Space>
                  </>
                ),
              },
              {
                key: "privacy",
                label: `Privacy Policy${docs?.privacy?.version ? ` (v${docs.privacy.version})` : ""}`,
                children: (
                  <>
                    <Form.Item
                      name="privacyTitle"
                      label="Title"
                      rules={[{ required: true, message: "Title is required." }]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      name="privacyEffective"
                      label="Effective date (UTC)"
                      rules={[{ required: true, message: "Effective date is required." }]}
                    >
                      <DatePicker showTime style={{ width: "100%" }} />
                    </Form.Item>

                    <Form.Item
                      name="privacyHtml"
                      label="Content"
                      rules={[
                        {
                          validator: (_, html) =>
                            hasMeaningfulText(html)
                              ? Promise.resolve()
                              : Promise.reject(new Error("Privacy content is required.")),
                        },
                      ]}
                      valuePropName="value"
                      trigger="onChange"
                      getValueFromEvent={(html) => html}
                    >
                      <LegalRichEditor minHeight={320} />
                    </Form.Item>

                    <Space>
                      <Button onClick={() => upsert(TYPE_PRIVACY, false)} loading={busy}>
                        Save (no version bump)
                      </Button>
                      <Button type="primary" onClick={() => upsert(TYPE_PRIVACY, true)} loading={busy}>
                        Publish new version
                      </Button>
                      <Button onClick={load} loading={busy}>
                        Reload
                      </Button>
                    </Space>
                  </>
                ),
              },
            ]}
          />
        </Form>
      </Card>
    </div>
  );
}
